/**
 * Lead Drop Popup / Form 2001 — CMS field configuration.
 *
 * Covers the same ground as popup 2 but built from field factories instead of
 * ~200 hand-written literals, so a label or default can only be wrong in one
 * place. The popup and the inline form share `LEAD_DROP_2001_SHARED_FIELDS`;
 * only the auto-open triggers are popup-only.
 *
 * EVERY field is `isJsonField: true` — i.e. it lands in `additional_fields`.
 * That is deliberate:
 *   - `lead_drop_popup` and `lead_drop_form` have DIFFERENT column sets, so a
 *     shared definition that wrote real columns would silently drop values on
 *     whichever table lacks them (Sequelize ignores unknown attributes).
 *   - Both tables already have `additional_fields`, and the public endpoint
 *     flattens it to the top level before the widget sees it
 *     (services/universitySettings.js), so the widget reads them exactly as if
 *     they were columns.
 *   - Net effect: this variant needs no schema migration at all.
 */

type FieldType =
  | "INPUT"
  | "TEXT_AREA"
  | "COLOR"
  | "FILE"
  | "DATE"
  | "TIME"
  | "CHECKBOX";

interface FieldDef {
  fieldName: string;
  label: string;
  value: string | number | boolean;
  valueType: "string" | "number" | "boolean";
  filedType: FieldType;
  placeholder: string;
  required: boolean;
  isJsonField: true;
  defaultValue: string | number | boolean;
  rows?: number;
  /**
   * Hint shown UNDER the label by FieldRenderer. Use this, not `placeholder`,
   * for anything an editor must read: a placeholder disappears the moment the
   * field has a value, so guidance written there is invisible exactly when the
   * field is configured.
   */
  description?: string;
}

const text = (
  fieldName: string,
  label: string,
  defaultValue = "",
  placeholder = `Enter ${label}`,
  description?: string,
): FieldDef => ({
  fieldName,
  label,
  value: defaultValue,
  valueType: "string",
  filedType: "INPUT",
  placeholder,
  required: false,
  isJsonField: true,
  defaultValue,
  ...(description ? { description } : {}),
});

const area = (
  fieldName: string,
  label: string,
  defaultValue = "",
  rows = 3,
): FieldDef => ({
  fieldName,
  label,
  value: defaultValue,
  valueType: "string",
  filedType: "TEXT_AREA",
  placeholder: `Enter ${label}`,
  required: false,
  isJsonField: true,
  defaultValue,
  rows,
});

const color = (fieldName: string, label: string, defaultValue: string): FieldDef => ({
  fieldName,
  label,
  value: defaultValue,
  valueType: "string",
  filedType: "COLOR",
  placeholder: `Enter ${label} (e.g. ${defaultValue})`,
  required: false,
  isJsonField: true,
  defaultValue,
});

const file = (fieldName: string, label: string): FieldDef => ({
  fieldName,
  label,
  value: "",
  valueType: "string",
  filedType: "FILE",
  placeholder: `Upload ${label}`,
  required: false,
  isJsonField: true,
  defaultValue: "",
});

const flag = (
  fieldName: string,
  label: string,
  placeholder: string,
  defaultValue = false,
): FieldDef => ({
  fieldName,
  label,
  value: defaultValue,
  valueType: "boolean",
  filedType: "CHECKBOX",
  placeholder,
  required: false,
  isJsonField: true,
  defaultValue,
});

/* ── Shared: everything both placements render ──────────────────────────── */

export const LEAD_DROP_2001_SHARED_FIELDS: FieldDef[] = [
  // ── Header strip + countdown ──
  text("form_title", "Header Text", "Admission Closed on 02 Feb"),
  color("form_title_color", "Header Text Color", "#FFFFFF"),
  {
    ...text("end_date", "Countdown End Date"),
    filedType: "DATE",
    placeholder: "Leave empty to hide the countdown",
  },
  {
    ...text("end_time", "Countdown End Time"),
    filedType: "TIME",
    placeholder: "Defaults to end of day",
  },
  color("date_background_color", "Countdown Box Color", "#FFFFFF"),
  color("date_number_color", "Countdown Number Color", "#222222"),
  color("date_text_color", "Countdown Label Color", "#222222"),

  // ── Card ──
  // Drives BOTH faces: the form card's header and every success screen's
  // background. The two use different angles by design; only the colours are
  // shared, so an editor sets the brand pair once.
  color("gradient_start", "Card Gradient Start", "#03A4B5"),
  color("gradient_end", "Card Gradient End", "#342B7C"),
  text("close_icon_alt", "Close Button Alt Text", "Close"),

  // ── Step 1 heading ──
  text("title_2", "Title", "Adapt Early or Fall Behind"),
  color("title_2_color", "Title Color", "#222222"),
  area(
    "description",
    "Sub Text",
    "60% of business tasks are becoming AI-driven, stay relevant!",
    2,
  ),
  color("description_color", "Sub Text Color", "rgba(34,34,34,0.8)"),

  // ── Step 1 field placeholders ──
  text("name_placeholder", "Name Placeholder", "Your Name"),
  text("email_placeholder", "Email Placeholder", "Email ID"),
  text("phone_placeholder", "Phone Placeholder", "Phone Number"),
  text(
    "qualification_placeholder",
    "Highest Qualification Placeholder",
    "Enter Highest Qualifications",
  ),
  text("course_placeholder", "Area of Interest Placeholder", "Enter Area of Interest"),

  // ── OTP ──
  text("otp_title", "OTP Label", "Enter 4-digit OTP Here"),
  text("otp_resend_prefix", "OTP Resend Prefix", "Didn't receive the OTP?"),
  text("resend_otp_text", "Resend OTP Text", "Resend OTP"),
  {
    ...text("resend_otp_seconds", "Resend OTP Countdown (seconds)", "30"),
    valueType: "number",
    placeholder: "Seconds before Resend OTP becomes clickable",
  },

  // ── CTA. Two labels: the course's Certification Programme flag picks one. ──
  text("button_step_1_text", "CTA Text — Regular Course", "Request A Call"),
  text(
    "button_certification_text",
    "CTA Text — Certification Programme",
    "Verify and Proceed",
  ),
  color("button_text_color", "CTA Text Color — Regular Course", "#FFFFFF"),
  color("button_background_color", "CTA Background Color — Regular Course", "#02A7B6"),
  color(
    "button_certification_text_color",
    "CTA Text Color — Certification Programme",
    "#FFFFFF",
  ),
  color(
    "button_certification_background_color",
    "CTA Background Color — Certification Programme",
    "#02A7B6",
  ),

  // ── Consent ──
  area(
    "checkbox_description",
    "Consent Text",
    "By clicking on Request A Call I allow the university to send program communication on email/sms/WhatsApp etc. I agree to the {{1:Terms & Conditions}} and {{2:Privacy Policy}}.",
  ),
  color("checkbox_description_color", "Consent Text Color", "#272323"),
  color("checkbox_color", "Consent Checkbox Color", "#02A7B6"),
  text(
    "terms_conditions_link",
    "Terms & Conditions Link",
    "",
    "Backs {{1:Terms & Conditions}} in the consent text",
  ),
  text(
    "privacy_policy_link",
    "Privacy Policy Link",
    "",
    "Backs {{2:Privacy Policy}} in the consent text",
  ),

  // ── Step 2 (certification programmes only) ──
  text("step2_title", "Step 2 — Title", "Tell us about your background"),
  area(
    "step2_subtitle",
    "Step 2 — Sub Text",
    "Helps us personalise your learning journey.",
    2,
  ),
  text(
    "year_placeholder",
    "Step 2 — Year of Qualification Placeholder",
    "Select Year of highest qualification",
  ),
  text("working_status_label", "Step 2 — Working Status Label", "Current Working status*"),
  text(
    "experience_placeholder",
    "Step 2 — Experience Placeholder",
    "Total work experience (in Years)",
  ),
  text("industry_placeholder", "Step 2 — Industry Placeholder", "Enter Your Industry Name"),
  text("company_placeholder", "Step 2 — Company Placeholder", "Enter Your Company Name"),
  text(
    "designation_placeholder",
    "Step 2 — Designation Placeholder",
    "Enter Your Designation",
  ),
  text("state_placeholder", "Step 2 — State Placeholder", "Select Your State"),
  text("city_placeholder", "Step 2 — City Placeholder", "Select Your City"),
  text("gender_placeholder", "Step 2 — Gender Placeholder", "Gender"),
  text("dob_placeholder", "Step 2 — Date of Birth Placeholder", "DD/MM/YYYY"),

  // ── Success cards ──
  file("success_image", "Success Image (300x300)"),
  text("success_image_alt", "Success Image Alt Text", "Programme"),

  text("new_user_title", "Success 1 — New Lead Title", "Thank You"),
  area(
    "new_user_description",
    "Success 1 — New Lead Description",
    "We've Received Your Details! Our team will reach out soon to help you move forward with your application.",
  ),

  text("existing_user_title", "Success 2 — Returning Lead Title", "Welcome Back!"),
  area(
    "existing_user_description",
    "Success 2 — Returning Lead Description",
    "You're Already Registered. Our admissions team will be in touch shortly. Until then, explore the program to learn what awaits you.",
  ),

  text("download_title", "Success 3 — Brochure Title", "Downloaded Successfully"),
  area(
    "download_description",
    "Success 3 — Brochure Description",
    // The {click here} token MUST stay — it renders the manual-download link,
    // which is the only recovery path when the automatic download is blocked by
    // the browser. This default is seeded into the row on save and therefore
    // OVERRIDES the component's fallback, so a copy here without the token
    // silently removes the link for every popup configured from now on.
    "Your download will begin automatically. You can {click here} to download it manually..",
  ),

  text("lms_success_title", "Success 4 — LMS Granted Title", "You're all set!"),
  area(
    "lms_success_description",
    "Success 4 — LMS Granted Description",
    "Welcome aboard - your learning journey starts now. Login details for LMS have been shared on your email.",
  ),

  // ── Success buttons — one pair per FLOW ──
  // Leave the text empty to hide the button (that is how the Figma success
  // cards ship). With "open in new tab" ON the link opens in a new tab; with it
  // OFF the button just closes the popup.
  // "Continue" matches the CMS default lead popup/form 2 ships for its new-user
  // and existing-user buttons, so editors moving between variants see the same
  // starting copy. Clear the field to hide the button entirely.
  flag(
    "success_button_enabled",
    "Show Success Button — Regular Flow",
    "Turn off to hide the button without losing its text and link",
    true,
  ),
  text(
    "success_button_text",
    "Success Button Text — Regular Flow",
    "Continue",
    "Shown on Thank You / Welcome Back / Brochure. Empty = no button",
  ),
  text("success_button_link", "Success Button Link — Regular Flow"),
  flag(
    "success_button_opens_new_tab",
    "Success Button Opens In New Tab — Regular Flow",
    "Off = the button closes the popup instead",
  ),
  color("success_button_background_color", "Success Button Background — Regular Flow", "#FFFFFF"),
  color("success_button_text_color", "Success Button Text Color — Regular Flow", "#222222"),

  flag(
    "certification_button_enabled",
    "Show Success Button — Certification Flow",
    "Turn off to hide the button without losing its text and link",
    true,
  ),
  text(
    "certification_button_text",
    "Success Button Text — Certification Flow",
    "Go to LMS",
    "Shown on the LMS granted / pending cards. Empty = no button",
  ),
  text(
    "certification_button_link",
    "Success Button Link — Certification Flow",
    "",
    "e.g. https://lms.example.com/login",
    // `description` renders under the label and stays visible once the field
    // has a value — a placeholder would vanish exactly when it is configured.
    "Fallback only. When the LMS returns a one-time sign-in link, the button uses that instead and opens it in a new tab, taking the learner straight into the LMS already signed in. This link is used only when no sign-in link comes back — set it to your LMS login page.",
  ),
  flag(
    // The hint lives in the LABEL: FieldRenderer's CHECKBOX branch renders the
    // label and nothing else, so a description or placeholder here is invisible.
    "certification_button_opens_new_tab",
    "Success Button Opens In New Tab — Certification Flow (an LMS sign-in link always opens in a new tab regardless)",
    "Off = the button closes the popup instead",
  ),
  color(
    "certification_button_background_color",
    "Success Button Background — Certification Flow",
    "#FFFFFF",
  ),
  color(
    "certification_button_text_color",
    "Success Button Text Color — Certification Flow",
    "#222222",
  ),

  text("lms_pending_title", "Success 5 — LMS Pending Title", "You're all set!"),
  area(
    "lms_pending_description",
    "Success 5 — LMS Pending Description",
    "We're setting up your access -login details will be sent to your registered email shortly",
  ),
];

/* ── Popup-only: auto-open triggers ─────────────────────────────────────── */

const POPUP_TRIGGER_FIELDS: FieldDef[] = [
  {
    ...text("popup_display_interval_1", "Popup Display Interval 1 (ms)"),
    valueType: "number",
    placeholder: "First auto-open delay in milliseconds",
  },
  {
    ...text("popup_display_interval_2", "Popup Display Interval 2 (ms)"),
    valueType: "number",
    placeholder: "Second auto-open delay in milliseconds",
  },
  {
    ...text("popup_display_interval_3", "Popup Display Interval 3 (ms)"),
    valueType: "number",
    placeholder: "Third auto-open delay in milliseconds",
  },
  flag("enable_delay_form", "Enable Delay Form", "Auto-open the popup after a delay"),
  flag("enable_exit_form", "Enable Exit Form", "Auto-open the popup on exit intent"),
];

const toDefaults = (fields: FieldDef[]) =>
  fields.reduce<Record<string, string | number | boolean>>((acc, f) => {
    acc[f.fieldName] = f.defaultValue;
    return acc;
  }, {});

export const LEAD_DROP_POPUP_2001_CONSTANTS = {
  DEFAULT_ROW: toDefaults([...LEAD_DROP_2001_SHARED_FIELDS, ...POPUP_TRIGGER_FIELDS]),
  LEAD_DROP_POPUP_FIELDS_CONFIG: [
    ...LEAD_DROP_2001_SHARED_FIELDS,
    ...POPUP_TRIGGER_FIELDS,
  ],
  METADATA_FIELDS: [] as FieldDef[],
};
